import { updateFcmToken } from "../actions/auth";
import { TOKEN_REFRESHED_EVENT } from "./authEvents";
import { fetchFcmToken } from "./firebase";

export const LAST_SENT_FCM_KEY = "rosiLastSentFcmToken";

const loggedInUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem("webUser"));
    return user?.data?.authToken ? String(user.data._id || "") : "";
  } catch (e) {
    return "";
  }
};

const readLastSent = () => {
  try {
    return localStorage.getItem(LAST_SENT_FCM_KEY);
  } catch (e) {
    return null;
  }
};

const writeLastSent = (value) => {
  try {
    if (value) localStorage.setItem(LAST_SENT_FCM_KEY, value);
    else localStorage.removeItem(LAST_SENT_FCM_KEY);
  } catch (e) {
    // storage unavailable: the next trigger just sends again
  }
};

let inFlight = false;

// Sends the browser's push token to the backend when it differs from the one
// last sent for this login. Login already sends the token it had; this covers
// a token that rotates later, permission granted after login, and sessions
// finished through the OTP step (which sends none). Never throws.
export const syncFcmToken = async (dispatch) => {
  const userId = loggedInUserId();
  if (!userId || inFlight) return false;
  inFlight = true;
  try {
    const token = await fetchFcmToken();
    if (!token) return false;
    const marker = `${userId}:${token}`;
    if (readLastSent() === marker) return false;
    await dispatch(updateFcmToken(token));
    // Only remember it if the same account is still logged in.
    if (loggedInUserId() === userId) writeLastSent(marker);
    return true;
  } catch (e) {
    return false;
  } finally {
    inFlight = false;
  }
};

// Runs the sync at app start, on login, after a token refresh, and whenever
// the tab becomes visible again (a rotated token or a newly granted permission shows up there).
// Forgets the last-sent token on logout. Returns a function that stops it.
export const startFcmTokenSync = (store) => {
  let currentUser = loggedInUserId();
  const run = () => syncFcmToken(store.dispatch);

  const unsubscribe = store.subscribe(() => {
    const userId = loggedInUserId();
    if (userId === currentUser) return;
    currentUser = userId;
    if (userId) run();
    else writeLastSent(null);
  });
  const onVisible = () => {
    if (document.visibilityState === "visible") run();
  };
  document.addEventListener("visibilitychange", onVisible);
  // A 403 drops the request that triggered a token refresh; run again now.
  window.addEventListener(TOKEN_REFRESHED_EVENT, run);
  run();

  return () => {
    unsubscribe();
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener(TOKEN_REFRESHED_EVENT, run);
  };
};
